// @flow

import { partial } from 'lodash';

import { isSubUrlOf } from '@zapier/url-utils';

import { createKeyFromService } from 'app/developer-v3/utils/utils';
import { getCurrentPathname } from 'app/common/utils/RouterSelectors';
import {
  BASE_APP_URL,
  BASE_APP_URL_LEGACY,
} from 'app/developer-v3/utils/constants';

import type { ActionType, ServiceType } from 'app/developer-v3/types';

/**
 * Builds the app URL based on ID and legacy check
 *
 * @param {number|string} appId
 * @param {boolean} isLegacy
 */
export const getAppUrl = (
  appId: number | string,
  isLegacy: boolean = false
): string => `${isLegacy ? BASE_APP_URL_LEGACY : BASE_APP_URL}/app/${appId}`;

/**
 * Given the app ID and the version number provided
 * returns the base app url
 *
 * @param {number|string} appId
 * @param {string} appVersionNumber
 */
export const getAppVersionUrl = (
  appId: number | string,
  appVersionNumber: string
): string => `${BASE_APP_URL}/app/${appId}/version/${appVersionNumber}`;

export const getBaseAuthUrl = (
  appId: number | string,
  appVersionNumber: string
): string => `${getAppVersionUrl(appId, appVersionNumber)}/authentication`;

export const getAuthUrlForFieldKey = (
  appId: number | string,
  appVersionNumber: string,
  fieldKey: string
): string => `${getBaseAuthUrl(appId, appVersionNumber)}/fields/${fieldKey}`;

export const getAdvancedUrl = (
  appId: number | string,
  appVersionNumber: string
) => `${getAppVersionUrl(appId, appVersionNumber)}/advanced`;

export const getServiceUrl = (
  type: ServiceType,
  serviceOptions: {
    id: string,
    version: string,
    [serviceKey: string]: string,
    actionType?: ActionType,
  }
) => {
  const { id: appId, version, actionType, actionKey } = serviceOptions;
  const serviceKey = serviceOptions[createKeyFromService(type)];
  const baseUrl = `${getAppVersionUrl(appId, version)}`;

  return actionType
    ? `${baseUrl}/actions/${actionType}/${actionKey}`
    : `${baseUrl}/${type}/${serviceKey}`;
};

export const getActionUrl = partial(getServiceUrl, 'actions');
export const getTriggerUrl = partial(getServiceUrl, 'triggers');

export const isVBOrLegacyUrl = (state: Object) =>
  isSubUrlOf(getCurrentPathname(state), BASE_APP_URL) ||
  isSubUrlOf(getCurrentPathname(state), BASE_APP_URL_LEGACY);

/**
 * Builds the url to link a user to a Godzilla page where
 * the violation happened.
 *
 * The deepLink is a string field that can be append to
 * the base URL /app/developer/app/:app_id/version/:version
 *
 * When deepLink is not provided, returns null
 *
 * Usage:
 * ```
 *   getDeepLinkUrl(1, '1.0.0', 'triggers/thing/input');
 *
 *   // returns: /app/developer/app/1/version/1.0.0/triggers/thing/input
 *
 *   getDeepLinkUrl(1, '1.0.0');
 *
 *   // returns: null
 * ```
 *
 * @param {string|number} appId
 * @param {string} appVersionNumber
 * @param {string} deepLink
 * @returns {string|null}
 */
export const getDeepLinkUrl = (
  appId: number | string,
  appVersionNumber: string,
  deepLink: string
) =>
  deepLink ? `${getAppVersionUrl(appId, appVersionNumber)}/${deepLink}` : null;
